// Burst view geometry: which captures belong together, and a piecewise
// time -> x mapping that keeps capture windows linear and squeezes the dead
// time between them into fixed-width breaks.
//
// All times are epoch seconds.

// Consecutive device triggers less than this apart are treated as one burst
// (the same fish on the same net). Checked against the NASS26 tagged fish:
// gaps under 3 minutes split none of them.
export const BURST_GAP_SECONDS = 180

// Capture windows closer than this stay linear instead of getting a break.
const MERGE_GAP_SECONDS = 3

// Context shown before the first and after the last capture.
export const BURST_CONTEXT_SECONDS = 60

export interface TriggerRef {
  captureId: string
  t: number
}

/** The run of triggers around `seedId` with every start-to-start gap under `maxGap`. */
export function burstAround(triggers: TriggerRef[], seedId: string, maxGap = BURST_GAP_SECONDS): TriggerRef[] {
  const sorted = [...triggers].sort((a, b) => a.t - b.t)
  const i = sorted.findIndex(r => r.captureId === seedId)
  if (i < 0) return []
  let lo = i
  let hi = i
  while (lo > 0 && sorted[lo].t - sorted[lo - 1].t < maxGap) lo--
  while (hi < sorted.length - 1 && sorted[hi + 1].t - sorted[hi].t < maxGap) hi++
  return sorted.slice(lo, hi + 1)
}

export type RowKind = 'seg' | 'break'

/** One stretch of the strip: time [t0, t1] drawn over x [x0, x1]. */
export interface LayoutRow {
  kind: RowKind
  t0: number
  t1: number
  x0: number
  x1: number
}

export interface BurstLayout {
  rows: LayoutRow[]
  width: number
  pxPerSecond: number
}

/** Merge capture windows that nearly touch into linear segments. */
export function mergeWindows(windows: [number, number][]): [number, number][] {
  const out: [number, number][] = []
  for (const w of [...windows].sort((a, b) => a[0] - b[0])) {
    const last = out[out.length - 1]
    if (last && w[0] - last[1] < MERGE_GAP_SECONDS) last[1] = Math.max(last[1], w[1])
    else out.push([w[0], w[1]])
  }
  return out
}

/** Break width grows with the log of the gap, so a minute reads wider than 8 s. */
function breakWidth(base: number, gap: number): number {
  return base * (0.6 + 0.6 * Math.min(1, Math.log10(Math.max(gap, 1)) / Math.log10(600)))
}

export function makeLayout(
  segments: [number, number][],
  leadT0: number,
  tailT1: number,
  pxPerSecond: number,
  breakBase: number,
  x0 = 6,
): BurstLayout {
  const rows: LayoutRow[] = []
  let x = x0
  let prev = leadT0
  for (const [a, b] of segments) {
    const bw = breakWidth(breakBase, a - prev)
    rows.push({ kind: 'break', t0: prev, t1: a, x0: x, x1: x + bw })
    x += bw
    rows.push({ kind: 'seg', t0: a, t1: b, x0: x, x1: x + (b - a) * pxPerSecond })
    x += (b - a) * pxPerSecond
    prev = b
  }
  const bw = breakWidth(breakBase, tailT1 - prev)
  rows.push({ kind: 'break', t0: prev, t1: tailT1, x0: x, x1: x + bw })
  x += bw
  return { rows, width: Math.ceil(x + 12), pxPerSecond }
}

/** x for any time on the strip (breaks map linearly too); null outside it. */
export function xAt(layout: BurstLayout, t: number): number | null {
  for (const r of layout.rows) {
    if (t >= r.t0 && t <= r.t1) return r.x0 + ((t - r.t0) / (r.t1 - r.t0 || 1)) * (r.x1 - r.x0)
  }
  return null
}

/** x for a time inside a capture segment; null if it falls in a break. */
export function segX(layout: BurstLayout, t: number): number | null {
  for (const r of layout.rows) {
    if (r.kind === 'seg' && t >= r.t0 && t <= r.t1) return r.x0 + (t - r.t0) * layout.pxPerSecond
  }
  return null
}

export function tAt(layout: BurstLayout, x: number): number | null {
  for (const r of layout.rows) {
    if (x >= r.x0 && x <= r.x1) return r.t0 + ((x - r.x0) / (r.x1 - r.x0 || 1)) * (r.t1 - r.t0)
  }
  return null
}

/**
 * Move `t` by `dt` seconds, hopping across breaks: they're dead time, so the
 * cursor jumps from one segment's edge to the next one's.
 */
export function stepTime(layout: BurstLayout, t: number, dt: number): number {
  const segs = layout.rows.filter(r => r.kind === 'seg')
  const i = segs.findIndex(s => t >= s.t0 - 1e-6 && t <= s.t1 + 1e-6)
  const n = t + dt
  if (i < 0) return n
  if (n > segs[i].t1) return i + 1 < segs.length ? segs[i + 1].t0 : segs[i].t1
  if (n < segs[i].t0) return i > 0 ? segs[i - 1].t1 : segs[i].t0
  return n
}

const TICK_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300]

/** Seconds between time-axis labels so they're at least `minPx` apart. */
export function tickStep(pxPerSecond: number, minPx = 64): number {
  return TICK_STEPS.find(s => s * pxPerSecond >= minPx) ?? TICK_STEPS[TICK_STEPS.length - 1]
}

/** Round-number ticks covering [lo, hi], about `n` of them. */
export function niceTicks(lo: number, hi: number, n: number): number[] {
  const raw = (hi - lo) / Math.max(n, 1)
  if (!(raw > 0)) return [lo]
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = ([1, 2, 2.5, 5, 10].find(m => m * mag >= raw) ?? 10) * mag
  const out: number[] = []
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(Number(v.toFixed(10)))
  return out
}

export function fmtGap(s: number): string {
  if (s < 60) return `${Math.round(s)} s`
  return `${Math.floor(s / 60)}m ${String(Math.floor(s % 60)).padStart(2, '0')}s`
}

export function fmtGapShort(s: number): string {
  return s < 60 ? `${Math.round(s)}s` : `${Math.round(s / 60)}m`
}

export function fmtCounts(v: number): string {
  const a = Math.abs(v)
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (a >= 1e4) return `${Math.round(v / 1e3)}k`
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}k`
  return String(Math.round(v))
}
